/**
 * ログインの段階別の観測（Sentry）。指示書 A7。
 *
 * 守ること（指示書 v1.5 2.1節・A7）:
 *   - 観測は、ログインの結果を変えない。ここの関数は例外を投げず、Promiseも返さない（呼び出し側は await しない）。
 *   - 観測のために認証APIを呼ばない（getUser / getSession 等は使わない）。
 *   - 利用者のパスワードログイン操作（handleLogin）からだけ試行を始める。
 *     セッション復元・トークン更新・別タブ・パスワード再設定の認証イベントでは、試行を始めない。
 *   - 送る情報は、試行ID・段階・経過ミリ秒・分類・HTTPステータス・ルート名・起動方法・復帰の有無だけ。
 *     メールアドレス・パスワード・トークン・URLのquery/hash・ユーザー情報は送らない
 *     （既存の Sentry.setUser の email も、このイベントからは外す）。
 *   - 正常なログインはエラーとして送らない（replaysOnErrorSampleRate が 1.0 のため）。
 *     正常・異常とも、試行全体を1つのトランザクションとして記録し、所要時間を集計できるようにする。
 *     止まった・失敗した試行だけ、warning のイベントとして送る。
 *   - 監視タイマーは、イベントを送るだけ。ログアウト・再認証・画面遷移は起こさない。
 */
import * as Sentry from '@sentry/vue'

export const LOGIN_STALL_MS = 30000

/** 試行を終わらせる段階（これ以降は記録しない） */
const TERMINAL_OUTCOME_BY_STAGE = {
  auth_failed: 'auth_failed',
  navigation_failed: 'navigation_failed',
  top_required_data_ready: 'top_ready',
  top_required_data_failed: 'top_failed'
}

/** 失敗としてイベントを送る結果 */
const FAILURE_OUTCOMES = new Set(['navigation_failed', 'top_failed', 'returned_to_login'])

/** @type {null | { id: string, entry: string, startedAt: number, stage: string, marks: Record<string, number>, info: Record<string, unknown>, span: any, stallTimer: any, stalledReported: boolean, wentBackground: boolean, onVisibility: any }} */
let current = null

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now())

const safe = (fn) => {
  try {
    const r = fn()
    // 念のため Promise が返った場合も、reject を外へ出さない
    if (r && typeof r.then === 'function') r.then(undefined, () => {})
    return r
  } catch (_) {
    return undefined
  }
}

const randomId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  } catch (_) { /* fallthrough */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const launchMode = () => {
  try {
    if (typeof window === 'undefined') return 'unknown'
    const standalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (typeof navigator !== 'undefined' && navigator.standalone === true)
    return standalone ? 'home_screen' : 'browser'
  } catch (_) {
    return 'unknown'
  }
}

/** 認証エラーの分類（パスワード違いとアプリ障害を分けて集計するため） */
export function classifyAuthError(error) {
  const msg = String(error?.message || '')
  const status = Number(error?.status)
  if (/タイムアウト|timeout/i.test(msg)) return 'timeout'
  if (/invalid login credentials/i.test(msg) || error?.code === 'invalid_credentials') return 'invalid_credentials'
  if (/email not confirmed/i.test(msg) || error?.code === 'email_not_confirmed') return 'email_not_confirmed'
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(msg)) return 'network'
  if (Number.isFinite(status) && status >= 500) return 'server'
  if (Number.isFinite(status) && status === 429) return 'rate_limited'
  return 'other'
}

/** 送ってよい項目だけに絞る（文字列は短く切る。メール・トークンらしき値は捨てる） */
const ALLOWED_INFO_KEYS = new Set(['route', 'from', 'reason', 'error_class', 'http_status', 'failure_type', 'stage'])
function sanitizeInfo(info) {
  const out = {}
  if (!info || typeof info !== 'object') return out
  for (const [k, v] of Object.entries(info)) {
    if (!ALLOWED_INFO_KEYS.has(k)) continue
    if (v == null) continue
    if (typeof v === 'number' || typeof v === 'boolean') { out[k] = v; continue }
    const s = String(v)
    if (/@|bearer|eyJ[a-zA-Z0-9_-]{10,}|[?#]/i.test(s)) continue
    out[k] = s.slice(0, 60)
  }
  return out
}

/** このイベントから、ユーザー情報と URL の query/hash を外す */
const stripPrivateFromEvent = (event) => {
  try {
    delete event.user
    if (event.request) {
      if (event.request.url) {
        const u = String(event.request.url)
        event.request.url = u.split(/[?#]/)[0]
      }
      delete event.request.query_string
      delete event.request.cookies
      delete event.request.headers
    }
  } catch (_) { /* 送信を止めない */ }
  return event
}

const withPrivateScope = (fn) => safe(() =>
  Sentry.withScope((scope) => {
    scope.setUser(null)
    scope.addEventProcessor(stripPrivateFromEvent)
    return fn(scope)
  })
)

const baseContext = (a) => ({
  attempt_id: a.id,
  entry: a.entry,
  stage: a.stage,
  elapsed_ms: Math.round(now() - a.startedAt),
  launch_mode: launchMode(),
  went_background: a.wentBackground,
  ...a.info
})

function sendFailureEvent(a, name) {
  withPrivateScope((scope) => {
    scope.setLevel('warning')
    scope.setTag('login_attempt_stage', a.stage)
    scope.setTag('login_launch_mode', launchMode())
    scope.setContext('login_attempt', { ...baseContext(a), marks: { ...a.marks } })
    Sentry.captureMessage(name)
  })
}

function clearTimers(a) {
  safe(() => { if (a.stallTimer) clearTimeout(a.stallTimer) })
  a.stallTimer = null
  safe(() => {
    if (a.onVisibility && typeof document !== 'undefined') document.removeEventListener('visibilitychange', a.onVisibility)
  })
  a.onVisibility = null
}

function endAttempt(a, outcome, info) {
  if (!a || current !== a) return
  current = null
  clearTimers(a)
  a.info = { ...a.info, ...sanitizeInfo(info) }
  safe(() => {
    if (!a.span) return
    a.span.setAttribute('login.outcome', outcome)
    a.span.setAttribute('login.total_ms', Math.round(now() - a.startedAt))
    a.span.setAttribute('login.went_background', a.wentBackground)
    if (outcome !== 'top_ready' && outcome !== 'navigated') a.span.setStatus({ code: 2, message: outcome })
    a.span.end()
  })
  if (FAILURE_OUTCOMES.has(outcome)) sendFailureEvent(a, `login_${outcome}`)
}

/**
 * パスワードログインの試行を始める（利用者の操作からだけ呼ぶ）。前の試行が残っていれば、置き換えとして閉じる。
 * @param {{ entry: string }} p entry: 'login' | 'manager_login'
 * @returns {string|null} 試行ID
 */
export function beginLoginAttempt({ entry } = {}) {
  return safe(() => {
    if (current) endAttempt(current, 'superseded')
    const a = {
      id: randomId(),
      entry: entry === 'manager_login' ? 'manager_login' : 'login',
      startedAt: now(),
      stage: 'login_started',
      marks: { login_started: 0 },
      info: {},
      span: null,
      stallTimer: null,
      stalledReported: false,
      wentBackground: false,
      onVisibility: null
    }
    current = a
    withPrivateScope(() => {
      a.span = Sentry.startInactiveSpan({
        name: 'login',
        op: 'login.flow',
        forceTransaction: true,
        attributes: { 'login.attempt_id': a.id, 'login.entry': a.entry, 'login.launch_mode': launchMode() }
      })
    })
    safe(() => Sentry.addBreadcrumb({ category: 'login', message: 'login_started', data: { attempt_id: a.id } }))
    a.stallTimer = setTimeout(() => {
      safe(() => {
        if (current !== a || a.stalledReported) return
        a.stalledReported = true
        sendFailureEvent(a, 'login_stalled')
      })
    }, LOGIN_STALL_MS)
    if (typeof document !== 'undefined' && document.addEventListener) {
      a.onVisibility = () => {
        safe(() => { if (document.visibilityState === 'hidden') a.wentBackground = true })
      }
      document.addEventListener('visibilitychange', a.onVisibility)
    }
    return a.id
  }) ?? null
}

/**
 * 段階を記録する。試行が無ければ何もしない（セッション復元などの遷移を、ログインとして数えない）。
 * @param {string} stage
 * @param {Record<string, unknown>} [info] route / reason / error_class / http_status / failure_type のみ送る
 */
export function markLoginStage(stage, info) {
  safe(() => {
    const a = current
    if (!a) return
    const elapsed = Math.round(now() - a.startedAt)
    if (a.marks[stage] == null) a.marks[stage] = elapsed
    a.stage = stage
    const clean = sanitizeInfo(info)
    a.info = { ...a.info, ...clean }
    safe(() => a.span && a.span.setAttribute(`login.t.${stage}`, elapsed))
    safe(() => Sentry.addBreadcrumb({ category: 'login', message: stage, data: { attempt_id: a.id, elapsed_ms: elapsed, ...clean } }))
    const terminal = TERMINAL_OUTCOME_BY_STAGE[stage]
    if (terminal) endAttempt(a, terminal, clean)
  })
}

/** ルーターの遷移が確定したとき（afterEach）。トップ以外に着いたら、その時点で試行を終える。 */
export function noteLoginNavigationSettled({ routeName, failureType } = {}) {
  safe(() => {
    const a = current
    if (!a) return
    if (failureType) {
      markLoginStage('navigation_failed', { route: routeName, failure_type: failureType })
      return
    }
    // ログイン画面の初期表示（試行前）の遷移は対象外。試行中にログイン画面へ戻された場合は失敗として扱う
    markLoginStage('access_check_completed', { route: routeName })
    markLoginStage('navigation_completed', { route: routeName })
    if (routeName === 'Login' || routeName === 'ManagerLogin') {
      endAttempt(a, 'returned_to_login', { route: routeName })
    } else if (routeName !== 'Top') {
      endAttempt(a, 'navigated', { route: routeName })
    }
    // Top は、必須データの読み込み（top_required_data_ready / failed）で終える
  })
}

/** ルーターに観測を付ける（遷移の開始・確定を記録するだけで、遷移の結果は変えない） */
export function attachLoginTelemetryToRouter(router) {
  safe(() => {
    router.beforeEach((to) => {
      safe(() => {
        if (current && current.marks.access_check_started == null) {
          markLoginStage('access_check_started', { route: to?.name ? String(to.name) : undefined })
        }
      })
      return true
    })
    router.afterEach((to, _from, failure) => {
      safe(() => {
        if (!current) return
        // 同じ場所への重複遷移（duplicated）は、失敗ではない
        const type = failure ? failureTypeName(failure) : null
        if (type === 'duplicated') return
        noteLoginNavigationSettled({ routeName: to?.name ? String(to.name) : undefined, failureType: type })
      })
    })
  })
}

function failureTypeName(failure) {
  // vue-router の NavigationFailureType: aborted=4, cancelled=8, duplicated=16
  const t = Number(failure?.type)
  if (t === 4) return 'aborted'
  if (t === 8) return 'cancelled'
  if (t === 16) return 'duplicated'
  return 'unknown'
}

/**
 * ルーターのガードが期限内に終わらなかったこと（ログイン試行の有無に関係なく）を warning で送る。
 * 送るのはルート名だけ。ユーザー情報・URLの query/hash は外す。
 */
export function reportGuardDeadline({ routeName } = {}) {
  withPrivateScope((scope) => {
    scope.setLevel('warning')
    scope.setTag('router_guard_deadline_route', routeName ? String(routeName).slice(0, 60) : 'unknown')
    scope.setTag('login_launch_mode', launchMode())
    scope.setContext('router_guard', { route: routeName ? String(routeName).slice(0, 60) : null, in_login_attempt: Boolean(current) })
    Sentry.captureMessage('router_guard_deadline')
  })
}

/** テスト用 */
export function _getCurrentLoginAttemptForTest() {
  return current
}
export function _resetLoginTelemetryForTest() {
  if (current) clearTimers(current)
  current = null
}
